import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContextLazy'
import { supabase } from '../../lib/supabase-auth'
import { ShieldCheckIcon, ArrowLeftIcon } from '@heroicons/react/24/outline'
import { CheckCircleIcon } from '@heroicons/react/24/solid'
import { generateDeviceHash } from '../../lib/device-fingerprint'

// Constants
const ISSUER_DOMAIN = 'pcmfinancial.ca'
const ISSUER_NAME = 'PCM'
const MAX_RETRY_ATTEMPTS = 3

export const MFAEnrollment: React.FC = () => {
  const [totpSecret, setTotpSecret] = useState('')
  const [verificationCode, setVerificationCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [setupComplete, setSetupComplete] = useState(false)
  const [isForced, setIsForced] = useState(false)
  const [pendingSession, setPendingSession] = useState<any>(null)
  const [referrer, setReferrer] = useState('/login')
  const [trustDevice, setTrustDevice] = useState(true)
  const [deviceHash, setDeviceHash] = useState<string | null>(null)
  const [retryCount, setRetryCount] = useState(0)
  const { user } = useAuth()
  const navigate = useNavigate()

  // Detect macOS for Keychain
  const isMacOS = /Macintosh|MacIntel|MacPPC|Mac68K/i.test(navigator.userAgent) && !('ontouchend' in document)

  // Generate otpauth URL
  const getOtpauthUrl = useCallback(() => {
    const email = user?.email || pendingSession?.email || ''
    return `otpauth://totp/${encodeURIComponent(ISSUER_NAME)}:${encodeURIComponent(email)}?secret=${totpSecret}&issuer=${encodeURIComponent(ISSUER_DOMAIN)}`
  }, [user?.email, pendingSession?.email, totpSecret])

  // Get Apple-specific URL for direct Keychain integration
  const getAppleOtpauthUrl = useCallback(() => {
    return `apple-${getOtpauthUrl()}`
  }, [getOtpauthUrl])

  useEffect(() => {
    // Generate device hash
    const initializeDevice = async () => {
      try {
        const hash = await generateDeviceHash()
        setDeviceHash(hash)
        sessionStorage.setItem('device_hash', hash)
        if (import.meta.env.DEV) {
          console.log('MFA Enrollment - Device hash generated:', hash.substring(0, 8) + '...')
        }
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error('Failed to generate device hash:', error)
        }
      }
    }
    initializeDevice()

    // Check if this is a forced enrollment during login
    const pending = sessionStorage.getItem('pending_mfa_setup')
    if (pending) {
      const data = JSON.parse(pending)
      setIsForced(true)
      setPendingSession(data)

      // Check if user already has MFA set up
      const hasExistingMFA = !!(
        data.session?.user?.user_metadata?.totp_secret ||
        data.session?.user?.user_metadata?.mfa_enabled
      )

      if (hasExistingMFA) {
        if (import.meta.env.DEV) {
          console.log('User already has MFA set up, redirecting to login for verification')
        }
        sessionStorage.removeItem('pending_mfa_setup')
        sessionStorage.removeItem('mfa_referrer')
        window.location.replace('/login')
        return
      }
    }

    // Check where user came from
    const mfaReferrer = sessionStorage.getItem('mfa_referrer')
    if (mfaReferrer) {
      setReferrer(mfaReferrer)
    } else {
      const currentPath = window.location.pathname
      if (currentPath.includes('client-portal')) {
        setReferrer('/client-portal/login')
      }
    }
  }, [])

  useEffect(() => {
    // Generate TOTP secret after we have user or pending session
    if (user?.email || pendingSession?.email) {
      generateTOTPSecret()
    }
  }, [user, pendingSession])

  const generateTOTPSecret = () => {
    // Generate a random base32 secret (20 bytes = 32 chars in base32)
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567'
    let secret = ''
    const array = new Uint8Array(20)
    crypto.getRandomValues(array)

    for (let i = 0; i < array.length; i++) {
      secret += chars[array[i] % 32]
    }

    setTotpSecret(secret)
  }

  // Refresh session if expired
  const refreshSessionIfNeeded = async (): Promise<string | null> => {
    const { data: { session: currentSession } } = await supabase.auth.getSession()

    if (currentSession?.access_token) {
      return currentSession.access_token
    }

    // Try pending session
    if (pendingSession?.session?.access_token) {
      return pendingSession.session.access_token
    }

    // Try to refresh
    const { data: refreshData } = await supabase.auth.refreshSession()
    if (refreshData?.session?.access_token) {
      return refreshData.session.access_token
    }

    return null
  }

  const handleKeychainSetup = () => {
    try {
      // Use apple-otpauth:// for direct Keychain integration
      window.location.href = getAppleOtpauthUrl()
    } catch (err) {
      console.error('Failed to open Keychain:', err)
      setError('Failed to open Keychain. Please try again.')
    }
  }

  const verifyAndSave = async () => {
    if (verificationCode.length !== 6) {
      setError('Please enter a 6-digit code')
      return
    }

    // Prevent double submission
    if (loading) {
      return
    }

    setLoading(true)
    setError(null)

    try {
      // First verify the code works
      const { data: verifyData, error: verifyError } = await supabase.functions.invoke('verify-totp', {
        body: {
          secret: totpSecret,
          code: verificationCode
        }
      })

      if (verifyError || !verifyData?.valid) {
        const errorMsg = verifyError?.message || 'Invalid code'

        // Check if it's a timing issue
        if (errorMsg.includes('expired') || errorMsg.includes('time')) {
          setError('Code expired. Wait for a new code and try again.')
        } else {
          setError('Invalid code. Check your Keychain and try again.')
        }
        setLoading(false)
        return
      }

      // Get access token with retry logic
      let accessToken = await refreshSessionIfNeeded()

      if (!accessToken) {
        if (retryCount < MAX_RETRY_ATTEMPTS) {
          setRetryCount(prev => prev + 1)
          setError('Session expired. Please try again.')
          setLoading(false)
          return
        }
        setError('Authentication error. Please log in again.')
        setLoading(false)
        return
      }

      // Save the secret via edge function
      if (import.meta.env.DEV) {
        console.log('Saving TOTP secret via edge function...')
      }

      const { data: saveData, error: saveError } = await supabase.functions.invoke('save-totp-secret', {
        body: {
          totpSecret: totpSecret,
          metadata: {
            mfa_setup_date: new Date().toISOString()
          }
        },
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      })

      if (saveError || !saveData?.success) {
        console.error('Failed to save TOTP secret:', saveError)
        setError(`Failed to save MFA settings: ${saveError?.message || 'Please try again'}`)
        setLoading(false)
        return
      }

      if (import.meta.env.DEV) {
        console.log('TOTP secret saved successfully')
      }

      // Update current session's JWT
      await supabase.auth.updateUser({
        data: {
          totp_secret: totpSecret,
          mfa_setup_date: new Date().toISOString()
        }
      })

      // Register trusted device if checkbox is checked
      if (trustDevice) {
        try {
          const storedDeviceHash = sessionStorage.getItem('device_hash') || deviceHash

          if (storedDeviceHash) {
            const userAgent = navigator.userAgent
            const platform = navigator.platform
            let browser = 'Unknown'
            let os = 'Unknown'

            if (userAgent.includes('Chrome')) browser = 'Chrome'
            else if (userAgent.includes('Safari')) browser = 'Safari'
            else if (userAgent.includes('Firefox')) browser = 'Firefox'
            else if (userAgent.includes('Edge')) browser = 'Edge'

            if (platform.includes('Win')) os = 'Windows'
            else if (platform.includes('Mac')) os = 'macOS'
            else if (platform.includes('Linux')) os = 'Linux'
            else if (/iPhone|iPad|iPod/.test(userAgent)) os = 'iOS'
            else if (/Android/.test(userAgent)) os = 'Android'

            await supabase.functions.invoke('register-device', {
              body: {
                deviceHash: storedDeviceHash,
                deviceName: `${browser} on ${os}`,
                metadata: {
                  browser,
                  os,
                  platform,
                  screenResolution: `${window.screen.width}x${window.screen.height}`,
                  userAgent
                },
                trustDuration: 30
              },
              headers: {
                'Authorization': `Bearer ${accessToken}`,
                'x-device-hash': storedDeviceHash
              }
            })
          }
        } catch (deviceError) {
          // Don't fail MFA setup if device registration fails
          console.error('Failed to register device:', deviceError)
        }
      }

      setSetupComplete(true)

      if (isForced) {
        sessionStorage.setItem('mfa_verified', 'true')
        sessionStorage.removeItem('pending_mfa_setup')
        sessionStorage.removeItem('mfa_referrer')
      }

      // Redirect after success
      setTimeout(() => {
        if (isForced) {
          window.location.replace('/portal/clients')
        } else {
          navigate('/portal/security')
        }
      }, 2000)

    } catch (err) {
      console.error('MFA setup error:', err)
      setError('An error occurred. Please try again.')
      setLoading(false)
    }
  }

  const handleBack = async () => {
    await supabase.auth.signOut()
    sessionStorage.clear()
    localStorage.removeItem('supabase.auth.token')
    window.location.href = referrer
  }

  // Success state
  if (setupComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-900 to-blue-800 p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-6 text-center">
          <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-3" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Setup Complete</h2>
          <p className="text-sm text-gray-600">Your account is now secured with two-factor authentication.</p>
          <p className="text-xs text-gray-500 mt-3">
            {isForced ? 'Logging you in...' : 'Redirecting...'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-900 to-blue-800 p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-6">
        {/* Back Button */}
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors mb-6"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          <span className="text-sm font-medium">Back to Login</span>
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-blue-100 rounded-lg">
            <ShieldCheckIcon className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-gray-900">Two-Factor Authentication</h1>
            <p className="text-xs text-gray-600">Required for your account</p>
          </div>
        </div>

        {/* Keychain Setup Card */}
        {isMacOS && (
          <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <svg className="h-5 w-5 text-gray-700" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
              <div>
                <p className="text-sm font-medium text-gray-900">iCloud Keychain</p>
                <p className="text-xs text-gray-500">Syncs across your Apple devices</p>
              </div>
            </div>
            <button
              onClick={handleKeychainSetup}
              disabled={!totpSecret}
              className="w-full py-2.5 px-4 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Set Up with Keychain
            </button>
            <p className="text-xs text-gray-500 text-center mt-2">
              Opens System Settings to add verification codes
            </p>
          </div>
        )}

        {/* Verification Section */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Enter verification code
            </label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              autoComplete="one-time-code"
              value={verificationCode}
              onChange={(e) => {
                const code = e.target.value.replace(/\D/g, '').slice(0, 6)
                setVerificationCode(code)
                // Auto-submit when 6 digits entered
                if (code.length === 6 && !loading) {
                  setTimeout(() => verifyAndSave(), 300)
                }
              }}
              className="w-full px-4 py-3 bg-white border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-xl tracking-[0.5em] text-center text-gray-900"
              placeholder="000000"
              disabled={loading}
              autoFocus
            />
            <p className="mt-2 text-xs text-gray-500 text-center flex items-center justify-center gap-1">
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"/>
              </svg>
              Safari will autofill this code using Touch ID after you add to Keychain
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Trust Device */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={trustDevice}
              onChange={(e) => setTrustDevice(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
            />
            <span className="text-sm text-gray-700">Trust this device for 30 days</span>
          </label>

          {/* Complete Button */}
          <button
            onClick={verifyAndSave}
            disabled={loading || verificationCode.length !== 6}
            className="w-full py-3 px-4 bg-gradient-to-r from-amber-500 to-amber-600 text-white font-semibold rounded-lg hover:from-amber-600 hover:to-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Verifying...
              </span>
            ) : (
              'Complete Setup'
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
